/**
 * The +Bake catering menu — the single source of truth for what /catering
 * renders, what the route's JSON-LD advertises, and what the FAQ/schema quote.
 *
 * Prices here are DISPLAY ONLY. Medusa owns cart pricing: the page prefers a
 * product's `calculated_price` whenever the catalog resolved, and falls back to
 * these amounts when it did not (see `pages/catering.tsx`). The same holds for
 * `minQuantity` and `seasonalWindow` — the backend copies live in product
 * metadata (`catering_min_quantity`, `catering_window_start` /
 * `catering_window_end`) and win when present. What is written here is the
 * offline fallback so the page still renders a truthful menu when the Medusa
 * request fails.
 *
 * Items with `kind: "service"` have no Medusa product on purpose: Latte Station
 * and the Dessert Table are quoted, not carted.
 */

export type CateringSegment =
  | "offices"
  | "medical"
  | "schools"
  | "celebrations"

export type CateringDietary =
  | "vegetarian"
  | "contains-nuts"
  | "contains-pork"

export type CateringPricing =
  | { type: "per_unit"; amount: number; unitLabel: string; minQuantity?: number }
  | { type: "per_person"; amount: number; minQuantity: number; presets: number[] }
  | { type: "quote"; fromLabel: string }

/** "MM-DD" — a calendar window with no year, so it repeats annually. */
export type CateringSeasonalWindow = { start: string; end: string }

export type CateringMenuItem = {
  /** Medusa product handle for cart items; a slug for quote-only services. */
  id: string
  kind: "bundle" | "box" | "drink" | "service" | "seasonal"
  name: string
  /** One line under the name. */
  tagline: string
  /** Rendered as the "What's inside" list. */
  contents: string[]
  serves?: string
  pricing: CateringPricing
  /** Display only — checkout enforces the real minimum. */
  leadTimeHours: number
  dietary?: CateringDietary[]
  tag?: string
  /** Sub-grouping inside the boxes section. */
  group?: string
  image: { src: string; alt: string }
  /**
   * True while `image.src` names the final path but the file is not in the
   * repo yet — the page renders a styled placeholder instead of a broken
   * image, and the asset test skips the existence check for this item only.
   * Drop the flag in the same commit that adds the file.
   */
  photoPending?: boolean
  /** Fallback only — Medusa metadata wins when the catalog resolved. */
  seasonalWindow?: CateringSeasonalWindow
  segments: CateringSegment[]
}

const IMG = "/images/plusbake/catering"

/**
 * Route-level assets live here too, so the same filesystem test that checks
 * every `image.src` also covers them. The catering route imports `ogImage`
 * rather than keeping its own constant — a route-local path is exactly how the
 * previous OG image rotted to a file that no longer existed.
 */
export const CATERING_PAGE_ASSETS = {
  /**
   * Both are still pending the photo shoot (see `photoPending`). The hero
   * falls back to a gradient panel and the route falls back to the site OG
   * image until the files land; `pending` is what the asset test reads.
   */
  pending: true,
  ogImage: "/images/plusbake/optimized/og-catering-1200x630.jpg",
  hero: {
    src: "/images/plusbake/optimized/catering-hero-1280.webp",
    widths: [640, 828, 960, 1280, 1920] as const,
    srcSet: [640, 828, 960, 1280, 1920]
      .map((w) => `/images/plusbake/optimized/catering-hero-${w}.webp ${w}w`)
      .join(", "),
    alt: "Catering spread from +Bake Cafe set on a conference table.",
  },
}

export const CATERING_BUNDLES: CateringMenuItem[] = [
  {
    id: "catering-bundle-morning-meeting",
    kind: "bundle",
    name: "Morning Meeting",
    tagline: "Pastry, cookies and coffee — the standing Monday order.",
    contents: [
      "1 pastry per person — butter croissant, almond croissant, or blueberry crumble muffin",
      "2 mini chocolate chip cookies per person",
      "Drip coffee: one 96 oz traveler per 12 guests, with cups, creamers and sugars",
      "Napkins, plates and serving tongs included",
    ],
    serves: "10 or more",
    pricing: { type: "per_person", amount: 12, minQuantity: 10, presets: [10, 25, 50] },
    leadTimeHours: 48,
    dietary: ["vegetarian", "contains-nuts"],
    tag: "Most ordered",
    image: {
      src: `${IMG}/bundle-morning-meeting.webp`,
      alt: "Morning Meeting bundle for ten: croissants, muffins, mini cookies and a coffee traveler from +Bake Cafe.",
    },
    photoPending: true,
    segments: ["offices", "schools"],
  },
  {
    id: "catering-bundle-office-breakfast",
    kind: "bundle",
    name: "Office Breakfast",
    tagline: "A savory course and fruit on top of the pastry run.",
    contents: [
      "1 pastry per person — butter croissant, almond croissant, or blueberry crumble muffin",
      "1 savory per person — 2 longanisa rolls, a garlic cream cheese croissant, or half a turkey provolone croissant",
      "Seasonal fruit cup per person",
      "Drip coffee: one 96 oz traveler per 12 guests, with cups, creamers and sugars",
      "Individually boxed for $1 more per person",
    ],
    serves: "10 or more",
    pricing: { type: "per_person", amount: 16, minQuantity: 10, presets: [10, 25, 50] },
    leadTimeHours: 48,
    dietary: ["contains-pork", "contains-nuts"],
    image: {
      src: `${IMG}/bundle-office-breakfast.webp`,
      alt: "Office Breakfast bundle: pastries, longanisa rolls, fruit cups and coffee for ten.",
    },
    photoPending: true,
    segments: ["offices", "schools"],
  },
  {
    id: "catering-bundle-rep-lunch",
    kind: "bundle",
    name: "Rep Lunch",
    tagline: "Individually boxed rice bowls with a per-person receipt.",
    contents: [
      "Individually boxed rice bowl — Longanisa, Sweet Chick, or Tofu Mushroom",
      "Cucumber-tomato salad in every box",
      "1 mini mochi brownie, bottled water and cutlery per box",
      "Dietary label on every lid and an itemized per-person receipt",
    ],
    serves: "8 or more",
    pricing: { type: "per_person", amount: 21, minQuantity: 8, presets: [8, 12, 24] },
    leadTimeHours: 48,
    dietary: ["vegetarian", "contains-pork"],
    tag: "Boxed individually",
    image: {
      src: `${IMG}/bundle-rep-lunch.webp`,
      alt: "Rep Lunch boxes: individually packed rice bowls with a brownie, water and cutlery.",
    },
    photoPending: true,
    segments: ["medical", "offices"],
  },
]

export const CATERING_BOXES: CateringMenuItem[] = [
  {
    id: "catering-assorted-croissants-standard",
    kind: "box",
    name: "Assorted Croissant Box – Standard",
    tagline: "One flavor per box, twelve pieces.",
    contents: [
      "12 croissants in a single flavor",
      "Choose Almond, Honey Miso, or Garlic Cream Cheese",
    ],
    serves: "12 pieces",
    pricing: { type: "per_unit", amount: 56, unitLabel: "box" },
    leadTimeHours: 48,
    dietary: ["vegetarian", "contains-nuts"],
    tag: "Standard",
    group: "Croissants",
    image: {
      src: `${IMG}/croissant-standard-box.webp`,
      alt: "Standard assorted croissant catering box with twelve croissants from +Bake Cafe.",
    },
    segments: ["offices", "schools", "celebrations"],
  },
  {
    id: "catering-assorted-croissants-premium",
    kind: "box",
    name: "Assorted Croissant Box – Premium",
    tagline: "Every flavor, including the twice-baked premiums.",
    contents: [
      "12 assorted croissants across all flavors",
      "Up to 6 premium pieces — Chocolate Nutella and Pistachio Raspberry Jam",
    ],
    serves: "12 pieces",
    pricing: { type: "per_unit", amount: 59, unitLabel: "box" },
    leadTimeHours: 48,
    dietary: ["vegetarian", "contains-nuts"],
    tag: "Premium",
    group: "Croissants",
    image: {
      src: `${IMG}/croissant-premium-box.webp`,
      alt: "Premium assorted croissant catering box with chocolate and pistachio croissants from +Bake Cafe.",
    },
    segments: ["offices", "celebrations"],
  },
  {
    id: "catering-assorted-cookie-box",
    kind: "box",
    name: "Assorted Cookie Box",
    tagline: "Build the dozen yourself, or let the bakery pick.",
    contents: [
      "12 cookies across Best Chocolate Chip, Walnut Sea Salt, Ube Toffee, Birthday White Chocolate and S'mores",
    ],
    serves: "12 pieces",
    pricing: { type: "per_unit", amount: 42, unitLabel: "box" },
    leadTimeHours: 48,
    dietary: ["vegetarian", "contains-nuts"],
    tag: "Assorted",
    group: "Cookies & Brownies",
    image: {
      src: `${IMG}/assorted-cookie-box.webp`,
      alt: "Assorted cookie catering box with twelve cookies from +Bake Cafe.",
    },
    segments: ["schools", "offices", "celebrations"],
  },
  {
    id: "catering-mini-chocolate-chip-cookies-box",
    kind: "box",
    name: "Mini Chocolate Chip Cookie Box",
    tagline: "Forty bites for coffee service and dessert tables.",
    contents: ["40 mini chocolate chip cookies"],
    serves: "40 pieces",
    pricing: { type: "per_unit", amount: 18, unitLabel: "box" },
    leadTimeHours: 48,
    dietary: ["vegetarian"],
    tag: "Mini",
    group: "Cookies & Brownies",
    image: {
      src: `${IMG}/mini-chocolate-chip-cookie-box.webp`,
      alt: "Box of forty mini chocolate chip cookies from +Bake Cafe.",
    },
    segments: ["medical", "offices", "celebrations"],
  },
  {
    id: "catering-mini-mochi-brownies",
    kind: "box",
    name: "Mini Mochi Brownies",
    tagline: "Chewy mochi texture, deep chocolate.",
    contents: ["12 mini mochi brownies"],
    serves: "12 pieces",
    pricing: { type: "per_unit", amount: 22, unitLabel: "box" },
    leadTimeHours: 48,
    dietary: ["vegetarian"],
    tag: "Signature",
    group: "Cookies & Brownies",
    image: {
      src: `${IMG}/mini-mochi-brownie-box.webp`,
      alt: "Box of twelve mini mochi brownies from +Bake Cafe.",
    },
    segments: ["celebrations", "offices"],
  },
  {
    id: "catering-mini-cookie-brownie-combo",
    kind: "box",
    name: "Mini Cookie & Brownie Combo",
    tagline: "Both minis in one box.",
    contents: ["20 mini chocolate chip cookies", "12 mini mochi brownies"],
    serves: "32 pieces",
    pricing: { type: "per_unit", amount: 32, unitLabel: "box" },
    leadTimeHours: 48,
    dietary: ["vegetarian"],
    tag: "Combo",
    group: "Cookies & Brownies",
    image: {
      src: `${IMG}/cookie-brownie-combo-box.webp`,
      alt: "Combo catering box of mini chocolate chip cookies and mini mochi brownies from +Bake Cafe.",
    },
    segments: ["offices", "schools"],
  },
  {
    id: "catering-longanisa-rolls",
    kind: "box",
    name: "Longanisa Rolls",
    tagline: "The savory box — sweet Filipino sausage in puff pastry.",
    contents: ["12 longanisa rolls"],
    serves: "12 pieces",
    pricing: { type: "per_unit", amount: 19, unitLabel: "box" },
    leadTimeHours: 48,
    dietary: ["contains-pork"],
    tag: "Savory",
    group: "Bakery & Cupcakes",
    image: {
      src: `${IMG}/longanisa-roll-box.webp`,
      alt: "Catering box of twelve longanisa rolls from +Bake Cafe.",
    },
    segments: ["offices", "celebrations"],
  },
  {
    id: "catering-blueberry-muffins-crumble",
    kind: "box",
    name: "Blueberry Muffins with Crumble",
    tagline: "Morning box — blueberry filling, crumble top.",
    contents: ["12 blueberry muffins with crumble topping"],
    serves: "12 pieces",
    pricing: { type: "per_unit", amount: 36, unitLabel: "box" },
    leadTimeHours: 48,
    dietary: ["vegetarian"],
    tag: "Morning",
    group: "Bakery & Cupcakes",
    image: {
      src: `${IMG}/blueberry-muffin-box.webp`,
      alt: "Catering box of twelve blueberry crumble muffins from +Bake Cafe.",
    },
    segments: ["schools", "offices"],
  },
  {
    id: "catering-assorted-seasonal-flavor-cupcakes",
    kind: "box",
    name: "Seasonal Cupcakes",
    tagline: "Flavors rotate — ask for the current selection.",
    contents: [
      "12 seasonal cupcakes",
      "Currently Vanilla Strawberry, Ube Caramel and Red Velvet",
    ],
    serves: "12 pieces",
    pricing: { type: "per_unit", amount: 42, unitLabel: "box" },
    leadTimeHours: 48,
    dietary: ["vegetarian"],
    tag: "Seasonal",
    group: "Bakery & Cupcakes",
    image: {
      src: `${IMG}/seasonal-cupcake-box.webp`,
      alt: "Catering box of twelve seasonal cupcakes from +Bake Cafe.",
    },
    segments: ["celebrations", "schools"],
  },
]

export const CATERING_DRINKS: CateringMenuItem[] = [
  {
    id: "catering-drink-coffee-traveler",
    kind: "drink",
    name: "Coffee Traveler",
    tagline: "96 oz of Local Coffee Roasters Brazil, ready to pour.",
    contents: [
      "96 oz batch-brewed Local Coffee Roasters Brazil",
      "12 cups with lids, creamers, sugars and stirrers",
    ],
    serves: "Serves 12",
    pricing: { type: "per_unit", amount: 28, unitLabel: "traveler" },
    leadTimeHours: 48,
    dietary: ["vegetarian"],
    image: {
      src: `${IMG}/drink-coffee-traveler.webp`,
      alt: "96 oz coffee traveler with cups, creamers and sugars from +Bake Cafe — serves 12.",
    },
    photoPending: true,
    segments: ["offices", "schools", "medical"],
  },
  {
    id: "catering-drink-cold-brew-jug",
    kind: "drink",
    name: "Cold Brew Jug",
    tagline: "A gallon of slow-steeped cold brew.",
    contents: ["1 gallon cold brew", "12 cups", "Ice on request"],
    serves: "Serves 12",
    pricing: { type: "per_unit", amount: 34, unitLabel: "jug" },
    leadTimeHours: 48,
    dietary: ["vegetarian"],
    image: {
      src: `${IMG}/drink-cold-brew-jug.webp`,
      alt: "One-gallon cold brew jug with cups from +Bake Cafe — serves 12.",
    },
    photoPending: true,
    segments: ["medical", "offices", "celebrations"],
  },
  {
    id: "catering-drink-pandan-sweetea-jug",
    kind: "drink",
    name: "Pandan Jasmine Sweetea Jug",
    tagline: "The house iced tea, by the gallon.",
    contents: ["1 gallon iced pandan jasmine sweetea", "12 cups"],
    serves: "Serves 12",
    pricing: { type: "per_unit", amount: 30, unitLabel: "jug" },
    leadTimeHours: 48,
    dietary: ["vegetarian"],
    tag: "House favorite",
    image: {
      src: `${IMG}/drink-pandan-sweetea-jug.webp`,
      alt: "One-gallon pandan jasmine sweetea jug with cups from +Bake Cafe — serves 12.",
    },
    photoPending: true,
    segments: ["celebrations", "offices"],
  },
]

export const CATERING_SERVICES: CateringMenuItem[] = [
  {
    id: "latte-station",
    kind: "service",
    name: "Latte Station",
    tagline: "A barista and an espresso cart at your event.",
    contents: [
      "2 hours of service, up to 60 drinks",
      "Pandan, ube, cookie butter and matcha lattes plus espresso classics, hot or iced",
      "Local Coffee Roasters beans, pulled on site",
      "$8 per drink past 60, $250 per extra hour",
      "Travel beyond 15 miles at $2 per mile; needs one standard outlet (generator $100)",
    ],
    serves: "Up to 60 drinks",
    pricing: { type: "quote", fromLabel: "From $750 · 2 hours" },
    leadTimeHours: 24 * 7,
    image: {
      src: `${IMG}/service-latte-station.webp`,
      alt: "+Bake Latte Station: barista pouring pandan, ube, cookie butter and matcha lattes at a mobile espresso cart.",
    },
    photoPending: true,
    segments: ["celebrations", "offices"],
  },
  {
    id: "dessert-table",
    kind: "service",
    name: "Dessert Table",
    tagline: "The full +Bake case, styled and set up for you.",
    contents: [
      "Mini mochi brownies, mini chocolate chip cookies and seasonal cupcakes",
      "Ube toffee cookie bites, mini fruit sando and pandan biscoff snickerdoodles",
      "Tiered stands, linens, setup and breakdown within 10 miles",
      "30 guest minimum",
    ],
    serves: "30 guests or more",
    pricing: { type: "quote", fromLabel: "From $14 per guest" },
    leadTimeHours: 24 * 7,
    image: {
      src: `${IMG}/service-dessert-table.webp`,
      alt: "+Bake dessert table with mini brownies, cookies, cupcakes and fruit sando on tiered stands.",
    },
    photoPending: true,
    segments: ["celebrations"],
  },
]

export const CATERING_SEASONAL: CateringMenuItem[] = [
  {
    id: "catering-gift-box-corporate",
    kind: "seasonal",
    name: "Corporate Gift Box",
    tagline: "Client and staff gifting, twelve boxes and up.",
    contents: [
      "6 assorted cookies and 6 mini mochi brownies",
      "4 oz bag of Local Coffee Roasters Brazil",
      "Blush tissue and a gift card with your message",
    ],
    serves: "One recipient",
    pricing: { type: "per_unit", amount: 42, unitLabel: "box", minQuantity: 12 },
    leadTimeHours: 48,
    dietary: ["vegetarian", "contains-nuts"],
    image: {
      src: `${IMG}/gift-box-corporate.webp`,
      alt: "Corporate gift box from +Bake Cafe: assorted cookies, mini mochi brownies and a bag of Local Coffee Roasters coffee.",
    },
    photoPending: true,
    segments: ["offices", "medical"],
  },
  {
    id: "catering-seasonal-simbang-gabi-box",
    kind: "seasonal",
    name: "Simbang Gabi Box",
    tagline: "For the nine mornings — pickup December 16–24.",
    contents: [
      "6 ube caramel cupcakes",
      "6 pandan biscoff snickerdoodles",
      "12 mini mochi brownies",
      "One 96 oz traveler of hot honey citron ginger tea with cups",
    ],
    serves: "Serves 10–12",
    pricing: { type: "per_unit", amount: 85, unitLabel: "box" },
    leadTimeHours: 48,
    dietary: ["vegetarian"],
    tag: "Seasonal",
    image: {
      src: `${IMG}/seasonal-simbang-gabi-box.webp`,
      alt: "Simbang Gabi box from +Bake Cafe: ube caramel cupcakes, pandan snickerdoodles, mini brownies and hot ginger tea.",
    },
    photoPending: true,
    seasonalWindow: { start: "11-15", end: "12-22" },
    segments: ["celebrations", "schools"],
  },
  {
    id: "catering-seasonal-appreciation-sleeves",
    kind: "seasonal",
    name: "Appreciation Sleeves",
    tagline: "Teacher and nurse appreciation, one sleeve per person.",
    contents: [
      "1 cookie and 1 mini mochi brownie in a clear-window kraft sleeve",
      'A "Thank you" card on every sleeve',
      "20 sleeve minimum",
    ],
    serves: "One per person",
    pricing: { type: "per_person", amount: 9, minQuantity: 20, presets: [20, 40, 80] },
    leadTimeHours: 48,
    dietary: ["vegetarian"],
    tag: "Seasonal",
    image: {
      src: `${IMG}/seasonal-appreciation-sleeves.webp`,
      alt: "Appreciation sleeves from +Bake Cafe: a cookie and a mini mochi brownie in a windowed kraft sleeve with a thank-you card.",
    },
    photoPending: true,
    seasonalWindow: { start: "04-15", end: "05-15" },
    segments: ["schools", "medical"],
  },
]

export const CATERING_MENU: CateringMenuItem[] = [
  ...CATERING_BUNDLES,
  ...CATERING_BOXES,
  ...CATERING_DRINKS,
  ...CATERING_SERVICES,
  ...CATERING_SEASONAL,
]

export const CATERING_BOX_GROUPS = [
  "Croissants",
  "Cookies & Brownies",
  "Bakery & Cupcakes",
] as const

export type CateringSegmentCard = {
  id: CateringSegment
  label: string
  blurb: string
  /** Item ids surfaced as "most ordered" shortcuts. */
  mostOrdered: string[]
  image: { src: string; alt: string }
  /** Same meaning as `CateringMenuItem.photoPending`. */
  photoPending?: boolean
}

export const CATERING_SEGMENT_CARDS: CateringSegmentCard[] = [
  {
    id: "offices",
    label: "Offices & meetings",
    blurb:
      "Monday breakfasts, all-hands, onboarding days. Priced by the person, packed to serve itself.",
    mostOrdered: [
      "catering-bundle-morning-meeting",
      "catering-bundle-office-breakfast",
      "catering-drink-coffee-traveler",
    ],
    image: {
      src: `${IMG}/segment-offices.webp`,
      alt: "Office breakfast catering from +Bake on a conference table.",
    },
    photoPending: true,
  },
  {
    id: "medical",
    label: "Medical offices",
    blurb:
      "Rep lunches with itemized per-person receipts, vegetarian options, and delivery at the time you book.",
    mostOrdered: [
      "catering-bundle-rep-lunch",
      "catering-mini-chocolate-chip-cookies-box",
      "catering-drink-cold-brew-jug",
    ],
    image: {
      src: `${IMG}/segment-medical.webp`,
      alt: "Rep lunch boxes lined up on a clinic break-room counter.",
    },
    photoPending: true,
  },
  {
    id: "schools",
    label: "Schools & community",
    blurb:
      "Staff appreciation, PD days, PTA mornings, parish fellowship.",
    mostOrdered: [
      "catering-seasonal-appreciation-sleeves",
      "catering-assorted-cookie-box",
      "catering-blueberry-muffins-crumble",
    ],
    image: {
      src: `${IMG}/segment-schools.webp`,
      alt: "Teacher appreciation spread: cookie box, muffin box and thank-you sleeves.",
    },
    photoPending: true,
  },
  {
    id: "celebrations",
    label: "Celebrations & events",
    blurb:
      "Debuts, baptisms, graduations and weddings: a dessert table and a Latte Station pouring pandan and ube.",
    mostOrdered: [
      "dessert-table",
      "latte-station",
      "catering-assorted-seasonal-flavor-cupcakes",
    ],
    image: {
      src: `${IMG}/segment-celebrations.webp`,
      alt: "Dessert table and Latte Station set up for a celebration.",
    },
    photoPending: true,
  },
]

export const CATERING_DIETARY_LABELS: Record<CateringDietary, string> = {
  vegetarian: "Vegetarian option",
  "contains-nuts": "Contains nuts",
  "contains-pork": "Contains pork",
}

/** Boxes and bundles ordered online. Services are quoted, not carted. */
export const CATERING_ONLINE_LEAD_TIME_HOURS = 48

const MONTH_DAY = /^(0[1-9]|1[0-2])-(0[1-9]|[12]\d|3[01])$/

const toMonthDay = (date: Date) =>
  `${String(date.getMonth() + 1).padStart(2, "0")}-${String(
    date.getDate(),
  ).padStart(2, "0")}`

/**
 * Windows carry no year, so a window whose start sorts after its end wraps the
 * new year (e.g. 12-01 → 01-15) and is inclusive on both ends.
 */
export function isWithinSeasonalWindow(
  window: CateringSeasonalWindow | undefined,
  now: Date = new Date(),
): boolean {
  if (!window) return true
  if (!MONTH_DAY.test(window.start) || !MONTH_DAY.test(window.end)) return true

  const today = toMonthDay(now)
  return window.start <= window.end
    ? today >= window.start && today <= window.end
    : today >= window.start || today <= window.end
}

/** Everything sellable today: seasonal items outside their window drop out. */
export function getActiveCateringItems(
  now: Date = new Date(),
  items: CateringMenuItem[] = CATERING_MENU,
): CateringMenuItem[] {
  return items.filter((item) => isWithinSeasonalWindow(item.seasonalWindow, now))
}

/** Services are quote-only and never reach the cart. */
export function isCartItem(item: CateringMenuItem): boolean {
  return item.pricing.type !== "quote"
}

/** The smallest quantity the item may be ordered in. */
export function getMinQuantity(item: CateringMenuItem): number {
  switch (item.pricing.type) {
    case "per_person":
      return item.pricing.minQuantity
    case "per_unit":
      return item.pricing.minQuantity ?? 1
    case "quote":
      return 0
  }
}

export function meetsMinimum(item: CateringMenuItem, quantity: number): boolean {
  if (!isCartItem(item)) return false
  return Number.isInteger(quantity) && quantity >= getMinQuantity(item)
}

/** Display total in dollars for a per-person item at the chosen headcount. */
export function bundleTotal(item: CateringMenuItem, headcount: number): number {
  if (item.pricing.type === "quote") return 0
  return item.pricing.amount * Math.max(headcount, 0)
}

export function findCateringItem(id: string): CateringMenuItem | undefined {
  return CATERING_MENU.find((item) => item.id === id)
}
